import { InfoBox } from '@/features/shared/components/InfoBox'
import { getQtyIncrementForProduct } from '@/features/shop/components/ProductCard/Utils'
import { ProductDetailBadges } from '@/features/shop/components/ProductDetail/ProductDetailBadges'
import { ProductDisclaimer } from '@/features/shop/components/ProductDetailCommon/ProductDisclaimer'
import { ProductImageCarousel } from '@/features/shop/components/ProductDetailCommon/ProductImageCarousel'
import { ProductWarningsAndDetails } from '@/features/shop/components/ProductDetailCommon/ProductWarningsAndDetails'
import { CMSLoaderPage } from '@/features/shop/components/Shelf/ShelfLoaders'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { getRetailerSearchUrl } from '@shared/constants/routes'
import { BOGO_QUANTITY_INCREMENT } from '@/features/shared/constants/global'
import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { useRemoveQueryString } from '@/features/shared/hooks/useRemoveQueryString'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { type ReactNode, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { BackButton } from '@/features/shop/components/ProductDetail/BackButton'
import { ProductDetailInstructions } from '@/features/shop/components/ProductDetail/ProductDetailInstructions'
import { ProductDetailPrice } from '@/features/shop/components/ProductDetail/ProductDetailPrice'
import { ProductDetailPromotionAndCoupon } from '@/features/shop/components/ProductDetail/ProductDetailPromotionAndCoupon'
import {
  trackProductViewed,
  useTrackProductWithDropdown,
} from '@/features/shared/analytics/products'
import { ProductDetailDeleteBuyAgain } from '@/features/shop/components/ProductDetail/ProductDetailDeleteBuyAgain'
import { useBasket } from '@/features/shared/hooks/useBasket'
import { useShouldConfirmAddress } from '@/features/account/services/Addresses/useShouldConfirmAddress'
import { useQueryProductNote } from '@/features/shop/services/ProductNote/queries'
import {
  pdpProductQueryOptions,
  useQueryPdpProduct,
} from '@/features/shop/services/Product/queries'
import { useActiveOrder } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { GridLayoutContainer } from '@/features/shared/styles/layout/container'
import { screenSizes } from '@shipt/design-system-themes'
import { getErrorMessage, getErrorStatus } from '@/features/shared/utils/dataFetching/utils'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useUpdateProductQuantity } from '@/features/shared/hooks/useUpdateProductQuantity'
import { type Product } from '@/features/shop/services/Product/types'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { ErrorPage } from '@/features/shared/components/ErrorPage'
import { useQueryPreference } from '@/features/shop/services/Substitutions/queries'
import isEmpty from 'lodash/isEmpty'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import {
  usePlaEligibility,
  useIsGuestWithZipOnly,
  useOpenGuestAddressModalForPLA,
} from '@/features/shop/components/ProductDetail/PLA/hooks'
import dynamic from 'next/dynamic'
import {
  PDP_ADD_TO_CART,
  PDP_ATC_CLICKED,
} from '@/features/shop/components/ProductDetail/constants'
import { useShallowRouterReplace } from '@/features/shared/hooks/useShallowRouterReplace'
import { Body } from '@shipt/design-system-typography'
import { ProductNoResultsState } from '@/features/shop/components/ProductNoResultsState'
import { ProductName } from '@/features/shop/components/ProductDetailCommon/ProductName'
import { DynamicOutOfStockModal } from '@/features/shop/components/Modal/OutOfStockModal/DynamicOutOfStockModal'
import { DynamicProductInstructionsModal } from '@/features/shop/components/Modal/ProductInstructionsModal/DynamicProductInstructionsModal'
import { SHELF_RENDER_TYPE } from '@/features/shared/constants/shelves'
import { useMutationUpdateCart } from '@/features/shop/services/Cart/mutations'
import { ProductDetailRatingInfo } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailRatingInfo'
import { ProductDetailDockedATC } from '@/features/shop/components/ProductDetail/ProductDetailDockedATC'
import { useProductDetailRatingsAndReviews } from '@/features/shop/components/ProductDetail/hooks/useProductDetailRatingsAndReviews'
import { ProductDetailRatingsAndReviewsSection } from '@/features/shop/components/ProductDetail/ProductDetailRatingsAndReviewsSection'
import { useHeaderRect } from '@/features/shared/hooks/useHeaderRect'
import { useQueryCMSPdpShelves } from '@/features/cms/services/CMS/queries'
import { LayoutGroup } from '@/features/cms/components/CMS/components/LayoutGroup'
import { useQueryClient } from '@tanstack/react-query'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'
import { useCouponSavings, type CouponClippedState } from '@/features/shop/services/Coupons/hooks'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { DynamicGuestAddressModal } from '@/features/account/components/Modal/GuestAddressModal/DynamicGuestAddressModal'
import { type GuestStore } from '@/features/shop/services/ShoppingStore/types'
import { datadogRum } from '@datadog/browser-rum'
import { getProductsAspectsForLogging } from '@/features/shop/services/Product/utils'
import {
  useIsUserZipOnlySignedInUser,
  useStoreId,
  useUserAddressParams,
} from '@/features/account/services/User/hooks'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { shoppingStoresQueryOptions } from '@/features/shop/services/ShoppingStore/queries'
import { DynamicGlobalAddressModal } from '@/features/account/components/Modal/GlobalAddressModal/DynamicGlobalAddressModal'
import { ProductDetailLoyaltyBanner } from '@/features/shop/components/ProductDetail/ProductDetailLoyaltyBanner'
import { useQueryRewardsPrograms } from '@/features/account/services/RewardsPrograms/queries'
import { DynamicConfirmAddressModal } from '@/features/account/components/Modal/ConfirmAddressModal/DynamicConfirmAddressModal'
import { FeedbackBanner } from '@shipt/design-system-banner'
import { ProductSEOHead } from '@/features/shared/components/SEOHeader/ProductSEOHead'
import { Footer } from '@/features/shared/components/Footers/Footer'
import { ProductDetailVariation } from '@/features/shop/components/ProductDetail/ProductDetailVariation'
import { useVariationsState } from '@/features/shop/components/ProductVariation/useVariationsState'
import { ProductQuantityButton } from '@/features/shop/components/ProductQuantityButton'
import { useAutoOpenFreeItemPromoModal } from '@/features/shop/hooks/useAutoOpenFreeItemPromoModal'

const ProductDetailPlaContent = dynamic(() =>
  import('./PLA/ProductDetailPlaContent').then((m) => m.ProductDetailPlaContent),
)
function usePdpData(id: number) {
  const { isPending, isFetching, data, error } = useQueryPdpProduct(id)
  const productCoupon = data?.productCoupon
  const couponClippedState = useCouponSavings(productCoupon)
  return {
    isPending,
    isFetching,
    error,
    product: data?.product,
    productCoupon,
    couponClippedState,
  }
}

const isDateRangeActive = (startsOn: string, endsOn: string): boolean => {
  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/
  if (!dateOnlyPattern.test(startsOn) || !dateOnlyPattern.test(endsOn)) {
    return false
  }

  const today = new Date().toISOString().slice(0, 10)
  return startsOn <= today && today <= endsOn
}

export const ProductDetailComponent = ({ productId }: { productId: number }) => {
  const router = useSegwayRouter()

  const searchParams = useSearchQueryParams()

  const queryClient = useQueryClient()
  const storeParams = useStoreParams()
  const { isPending, isFetching, product, error, productCoupon, couponClippedState } =
    usePdpData(productId)
  useEffect(() => {
    if (product) {
      const { productCount: _, ...aspects } = getProductsAspectsForLogging([product])
      datadogRum.setViewContextProperty('product_metadata', aspects)
      datadogRum.setViewContextProperty('isPla', !!searchParams.region_id)
    }
  }, [product, searchParams.region_id])

  // GQL returns a 200 with no data if the product is not found
  if (error || (!isPending && !isFetching && !product)) {
    const errorStatus = getErrorStatus(error)
    if (errorStatus === 404 || (!product && !error))
      return (
        <ProductNoResultsState
          message="This product is unavailable at"
          productId={String(productId)}
        />
      )
    return <ErrorPage statusCode={errorStatus} />
  }
  if (isPending || isFetching) {
    return (
      <Row align="center" justify="center" marginTop="5xl">
        <LoadingIndicator size="xl" />
      </Row>
    )
  }

  if (product) {
    const handleSelectVariationProduct = (variationProduct: Product) => {
      const variationProducts = product.variationProducts ?? []
      queryClient.setQueryData(
        pdpProductQueryOptions({ productId: variationProduct.id, storeParams }).queryKey,
        (previousProduct) => {
          if (previousProduct) return undefined
          return {
            productCoupon,
            product: { ...variationProduct, variationProducts },
          }
        },
      )
      router.replace(
        getProductPageUrl({
          id: variationProduct.id,
          universal_id: variationProduct.universal_id,
        }),
        {
          shallow: true,
        },
      )
    }
    return (
      <ProductDetail
        isFetching={isFetching}
        product={product}
        productCoupon={productCoupon}
        couponClippedState={couponClippedState}
        onSelectVariationProduct={handleSelectVariationProduct}
        // update the key to remount the component when the product changes
        key={product.id}
      />
    )
  }
  return null
}

const getProductToUpdate = ({
  product,
  selectedProduct,
}: {
  product: Product
  selectedProduct: Product
}): Product => ({
  ...selectedProduct,
  // if the product is a variation product, we need to ensure we pass along the parent product's
  // couponId and cpgPromotion since the variation product does not have these fields
  couponId: product.couponId,
  cpgPromotion: product.cpgPromotion,
})

const ShelfLoaderWithChildren = ({
  isLoading,
  children,
}: {
  isLoading: boolean
  children: ReactNode
}) =>
  isLoading ? (
    <CMSLoaderPage types={[SHELF_RENDER_TYPE.PRODUCT, SHELF_RENDER_TYPE.PRODUCT]} />
  ) : (
    children
  )

export const ProductDetail = ({
  product,
  productCoupon,
  isFetching,
  couponClippedState,
  onSelectVariationProduct,
}: {
  isFetching: boolean
  product: Product
  productCoupon:
    | {
        couponId: string
        description?: string
        summary?: string
        expiredAt: string
        faceValue: number
        appliedProductQty: number
      }
    | undefined
  couponClippedState: CouponClippedState
  onSelectVariationProduct: (variationProduct: Product) => void
}) => {
  const searchParams = useSearchQueryParams()
  const { shallowRouterReplace } = useShallowRouterReplace()
  const isGuestWithZipOnly = useIsGuestWithZipOnly()
  const { openDialog } = useHandleDialog()
  const removeQueryString = useRemoveQueryString()
  const variationProducts = product.variationProducts ?? []
  const { data: shoppingStore } = useUserShoppingStore()

  const {
    handleSelectProperty,
    selectedProperties,
    selectedProduct,
    partialMatchProduct,
    setSelectedProduct,
  } = useVariationsState({
    product,
    variationProducts,
    onSelectVariationProduct,
  })
  const displayProduct = partialMatchProduct ?? selectedProduct
  const { isPending: isCMSShelvesPending, data: cmsShelves } = useQueryCMSPdpShelves(
    selectedProduct.id,
  )
  const trackProductUpdate = useTrackProductWithDropdown()
  const currentActiveOrder = useActiveOrder()
  const isPlaEligible = usePlaEligibility(product.id)
  const reviewStats = product.review_stats ?? undefined
  const queryClient = useQueryClient()
  const addressParams = useUserAddressParams()
  const isZipOnlyUser = useIsUserZipOnlySignedInUser()
  const { updateProductQuantity, updateProductError } = useUpdateProductQuantity()
  const { mutateAsync: updateCart } = useMutationUpdateCart()
  const persistedNote = useQueryProductNote({
    productId: selectedProduct.id,
    categoryId: selectedProduct.rootCategoryId,
  }).data?.product_note
  const productAddedToCartRef = useRef(false)
  const { data: substitutionPreferences = {} } = useQueryPreference(product.id)

  const { id: orderId } = currentActiveOrder ?? {}

  const { getProductQuantity, getProductNote, getProductPromotionMetadata } = useBasket()
  const productQuantity = getProductQuantity(selectedProduct)
  // we don't want to use persisted note for an order that has already been placed since
  // the note on the order may be different than the note currently saved for the product,
  // and we want to reflect what is on the order.
  const note = orderId
    ? getProductNote(selectedProduct)
    : getProductNote(selectedProduct) || persistedNote || ''
  const context = orderId ? 'order' : 'cart'
  const { name, brandName = '', bulletPoints = [], oos = false } = displayProduct
  const { bought, cpgPromotion, bogo, pricing } = selectedProduct
  const { markCartMutationTriggered } = useAutoOpenFreeItemPromoModal({
    promotionMetadata: getProductPromotionMetadata(selectedProduct),
  })

  const dockedAtcButtonRef = useRef<HTMLDivElement>(null)

  const {
    showRatingsAndReviews,
    isUnderline,
    isAtcVisible,
    getAtcButtonRef,
    scrollToRatingsAndReview,
  } = useProductDetailRatingsAndReviews({
    reviewStats,
    dockedAtcButtonRef,
  })

  useEffect(() => {
    trackProductViewed({
      product,
      displayFormat: DISPLAY_FORMAT.DETAIL,
    })
  }, [product])

  const addToCartQueryParam = searchParams[PDP_ADD_TO_CART]
  // Adds product to cart from ungated/PLA PDP flow
  useEffect(() => {
    if (
      !orderId &&
      addToCartQueryParam &&
      product.id &&
      selectedProduct.id &&
      !selectedProduct.oos &&
      !productAddedToCartRef.current
    ) {
      productAddedToCartRef.current = true

      removeQueryString(PDP_ADD_TO_CART)

      let newQuantity = productQuantity + 1
      // If the addToCart query param is a number, use that as the quantity to add
      if (!isNaN(Number(addToCartQueryParam))) {
        newQuantity = Number(addToCartQueryParam)
      }
      updateProductQuantity({
        product: getProductToUpdate({ product, selectedProduct }),
        quantity: newQuantity,
        itemUpdated: Boolean(productQuantity),
        showToastOnError: false,
      }).then(() => {
        if (newQuantity > productQuantity) {
          markCartMutationTriggered()
        }
      })
      trackProductUpdate({
        product: selectedProduct,
        newQuantity,
        currentQuantity: productQuantity,
        displayFormat: DISPLAY_FORMAT.DETAIL,
        subLocation: PDP_ADD_TO_CART,
      })
    }
  }, [
    product,
    removeQueryString,
    addToCartQueryParam,
    orderId,
    selectedProduct,
    productQuantity,
    markCartMutationTriggered,
    trackProductUpdate,
    updateProductQuantity,
  ])

  const openGuestAddressModalForPLA = useOpenGuestAddressModalForPLA()

  const handleGuestAddressModalSubmit = ({
    guestStore,
  }: {
    guestStore: GuestStore | undefined
  }) => {
    if (guestStore) {
      return shallowRouterReplace({ [PDP_ADD_TO_CART]: true })
    }
  }

  const handleUpdateProduct = async (
    quantity: number,
    updatedNote?: string,
    isDockedAtc?: boolean,
  ) => {
    if (isGuestWithZipOnly) {
      if (searchParams.region_id) {
        openGuestAddressModalForPLA({
          onSubmit: handleGuestAddressModalSubmit,
        })
      } else {
        openDialog(DynamicGuestAddressModal, {
          isCancellable: true,
          startChangeStep: true,
          showLoginLink: true,
          contentLabel: 'Enter your delivery address',
          subheading: 'Item availability and price may vary by location.',
          onSubmit: handleGuestAddressModalSubmit,
        })
      }
      await shallowRouterReplace({ [PDP_ATC_CLICKED]: true })
      // one off for the guest address modal flow below. we need atcClicked in the
      // router, so the header can show its cart indicator, but NOT in the address bar,
      // so refreshing does not re trigger the flow.
      const url = new URL(window.location.href)
      url.searchParams.delete(PDP_ATC_CLICKED)
      const href = `${url.pathname}${url.search}`
      window.history.replaceState({ ...window.history.state, url: href, as: href }, '', href)
      return
    }

    if (isZipOnlyUser) {
      const shoppingStores = queryClient.getQueryData(
        shoppingStoresQueryOptions(addressParams).queryKey,
      )
      // We will need the product's store to determine if it is available at the new address.
      // We capture it here, before the user has updated their address.
      const productsStore = shoppingStores?.delivery_stores.find((s) => s.id === product.storeId)
      openDialog(DynamicGlobalAddressModal, {
        contentLabel: 'Enter your delivery address',
        subheading: 'Item availability and price may vary by location.',
        hideSavedAddresses: true,
        selectedStoreId: productsStore?.id,
        selectedStoreName: productsStore?.name,
        destinationUrl: `${getProductPageUrl(product)}?${PDP_ADD_TO_CART}=true`,
      })
      return
    }
    if (shouldConfirmAddress) {
      return openDialog(DynamicConfirmAddressModal, {
        onContinue: () => shallowRouterReplace({ [PDP_ADD_TO_CART]: true }),
      })
    }

    const productForUpdate: Product = {
      ...getProductToUpdate({ product, selectedProduct }),
      quantity,
      note: updatedNote ?? note,
    }
    const shouldShowOutOfStockModal =
      product.lowStock && isEmpty(substitutionPreferences) && quantity > productQuantity
    if (shouldShowOutOfStockModal) {
      openDialog(DynamicOutOfStockModal, { product })
    }
    if (!orderId && productQuantity && productQuantity === quantity) {
      await updateCart(productForUpdate)
    } else {
      await updateProductQuantity({
        product: productForUpdate,
        quantity,
        itemUpdated: Boolean(productQuantity),
        showToastOnError: false,
      })
    }
    if (quantity > productQuantity && !shouldShowOutOfStockModal) {
      markCartMutationTriggered()
    }
    trackProductUpdate({
      product: selectedProduct,
      newQuantity: quantity,
      currentQuantity: productQuantity,
      displayFormat: DISPLAY_FORMAT.DETAIL,
      ...(isDockedAtc && { subLocation: 'docked_atc' }),
    })
  }

  const handleDockedAtcUpdateProduct = (quantity: number, updatedNote?: string) => {
    // Call the original handleUpdateProduct with isDockedAtc set to true for tracking
    handleUpdateProduct(quantity, updatedNote, true)
  }

  const handleInstructionsModalSubmit = (newNote = '') => {
    handleUpdateProduct(productQuantity || getQtyIncrementForProduct(selectedProduct), newNote)
  }

  const handleExperimentDrawer = () => {
    openDialog(DynamicProductInstructionsModal, {
      product: { ...selectedProduct, note },
      handleSubmit: handleInstructionsModalSubmit,
      ...(!productQuantity && { context }),
    })
  }

  const handleOpenProductInstructionsModal = () => {
    trackElementClicked({
      type: 'button',
      content: 'add note or backup',
      display_format: 'product_notes',
    })
    handleExperimentDrawer()
  }

  const errorMessage = getErrorMessage(
    updateProductError,
    'An error has occurred. Please try again',
  )

  const { update: updateHeader } = useHeaderRect({
    updateOnScroll: true,
  })

  const { price: loyaltyRewardsPrice, startsOn, endsOn } = pricing?.loyalty ?? {}
  const { data: rewardsData } = useQueryRewardsPrograms()
  const storeId = useStoreId()
  const unlinkedRewardsAccount = rewardsData?.available_programs.find(
    (program) => program.store.id === storeId,
  )
  const shouldConfirmAddress = useShouldConfirmAddress()

  const showLoyaltyRewardsBanner =
    !selectedProduct.onSale &&
    !!loyaltyRewardsPrice &&
    !!unlinkedRewardsAccount &&
    !!startsOn &&
    !!endsOn &&
    isDateRangeActive(startsOn, endsOn)

  const headerRect = updateHeader()
  const top = headerRect.height

  return (
    <>
      {!!showRatingsAndReviews && (
        <DockedATCContainer $wrapperTop={top} ref={dockedAtcButtonRef}>
          <ProductDetailDockedATC
            product={displayProduct}
            productPrice={<ProductDetailPrice product={displayProduct} />}
            isVisible={isAtcVisible}
          >
            <ProductQuantityButton
              isSelectionPartial={!!partialMatchProduct}
              context={context}
              increment={bogo ? BOGO_QUANTITY_INCREMENT : 1}
              value={productQuantity}
              product={selectedProduct}
              onClick={handleDockedAtcUpdateProduct}
              key={selectedProduct.id}
              isDockedAtc
            />
          </ProductDetailDockedATC>
        </DockedATCContainer>
      )}

      <ProductSEOHead {...product} />

      <GridLayoutContainer>
        <Box className="fullWidth contentAtTablet" padding={{ base: ['lg'], md: ['xxl', 0, 0] }}>
          <div>
            {!isPlaEligible && <BackButton />}
            <Stack wrap as="section">
              <ProductTopLeftWrapper>
                <ProductImageCarousel product={displayProduct} key={displayProduct.id} />
              </ProductTopLeftWrapper>
              <ProductTopRightWrapper>
                {!!brandName &&
                  (isGuestWithZipOnly ? (
                    <Body size="xl">{brandName}</Body>
                  ) : (
                    <Box marginBottom="xs">
                      <TextButtonLink
                        aria-label={`Click to view ${brandName} products`}
                        href={`${getRetailerSearchUrl(
                          shoppingStore?.name,
                        )}?brands=${encodeURIComponent(brandName)}`}
                      >
                        {brandName}
                      </TextButtonLink>
                    </Box>
                  ))}
                <Column spacing="lg">
                  <ProductName name={name} />
                  <ProductDetailWrapper>
                    <Box marginBottom="sm">
                      <ProductDetailPrice product={displayProduct} />
                      {!!showLoyaltyRewardsBanner && (
                        <Box marginTop="sm">
                          <ProductDetailLoyaltyBanner
                            loyaltyProgram={unlinkedRewardsAccount}
                            loyaltyRewardsPrice={loyaltyRewardsPrice}
                          />
                        </Box>
                      )}
                      {showRatingsAndReviews && reviewStats ? (
                        <Box marginTop="xs">
                          <ProductDetailRatingInfo
                            scrollToSection={scrollToRatingsAndReview}
                            avgRating={reviewStats.average_rating}
                            isUnderline={isUnderline}
                            totalRatings={reviewStats.total_ratings}
                          />
                        </Box>
                      ) : null}
                    </Box>
                    {variationProducts.length > 0 && (
                      <ProductDetailVariation
                        variationProducts={variationProducts}
                        handleSelectProperty={handleSelectProperty}
                        selectedProperties={selectedProperties}
                      />
                    )}
                    <ProductDetailBadges
                      product={displayProduct}
                      productQuantity={productQuantity}
                    />
                    <ProductDetailPromotionAndCoupon
                      coupon={productCoupon}
                      product={product}
                      selectedProduct={selectedProduct}
                      productQuantity={productQuantity}
                      couponClippedState={couponClippedState}
                    />
                  </ProductDetailWrapper>
                  {!oos && (
                    <div ref={getAtcButtonRef}>
                      <ProductQuantityButton
                        disabled={!!partialMatchProduct}
                        isSelectionPartial={!!partialMatchProduct}
                        context={context}
                        increment={bogo ? BOGO_QUANTITY_INCREMENT : 1}
                        value={productQuantity}
                        product={selectedProduct}
                        onClick={handleUpdateProduct}
                        // create a new component when product changes
                        // to reset internal state
                        key={selectedProduct.id}
                      />
                    </div>
                  )}
                  {bulletPoints.length > 0 && <InfoBox text={bulletPoints.join('. ')} />}
                  {!oos && !isGuestWithZipOnly && !isZipOnlyUser && !partialMatchProduct && (
                    <ProductDetailInstructions
                      product={selectedProduct}
                      note={note}
                      productQuantity={productQuantity}
                      handleOpenModal={handleOpenProductInstructionsModal}
                    />
                  )}
                  {!!updateProductError && (
                    <FeedbackBanner title={errorMessage} variant="negative" />
                  )}
                  {!!bought && !isGuestWithZipOnly && (
                    <Stack justify="center" spacing="lg" padding={['sm', 0]}>
                      <ProductDetailDeleteBuyAgain
                        product={selectedProduct}
                        setSelectedProduct={setSelectedProduct}
                      />
                    </Stack>
                  )}
                  {!!isPlaEligible && <ProductDetailPlaContent productId={product.id} />}
                </Column>
              </ProductTopRightWrapper>
            </Stack>
            <ProductWarningsAndDetails product={displayProduct} />
          </div>
        </Box>
      </GridLayoutContainer>
      <ShelfLoaderWithChildren isLoading={isFetching || isCMSShelvesPending}>
        {!!cmsShelves && <LayoutGroup {...cmsShelves} />}
      </ShelfLoaderWithChildren>
      <GridLayoutContainer>
        <Box className="fullWidth contentAtTablet" padding={{ base: ['lg'], md: ['xxl', 0, 0] }}>
          <div>
            <ProductDetailRatingsAndReviewsSection
              productId={product.id}
              reviewStats={reviewStats}
              showRatingsAndReviews={showRatingsAndReviews}
            />
            <ProductDisclaimer hasFeaturedPromotion={Boolean(cpgPromotion?.category_id)} />
          </div>
        </Box>
      </GridLayoutContainer>
      <Footer />
    </>
  )
}

const ProductTopLeftWrapper = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 1.5rem;
  padding-right: 1rem;

  @media ${screenSizes.tablet} {
    width: 60%;
  }
`

const ProductTopRightWrapper = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  @media ${screenSizes.tablet} {
    width: 40%;
    padding-left: 4rem;
    max-width: 27rem;
    margin-left: auto;
  }
`

const ProductDetailWrapper = styled.div`
  width: 100%;
`

const DockedATCContainer = styled.header<{
  $wrapperTop: number
}>`
  pointer-events: none;
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: 999;

  @media ${screenSizes.tablet} {
    top: ${({ $wrapperTop }) => $wrapperTop}px;
    bottom: auto;
  }
`
